import { useBreakpoints } from '@/Utility/hooks';
import { Box, LinearProgress } from '@mui/material';
import type React from 'react';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { AlertDialog, type AlertDialogProps, useAlertDialogRef } from '../ui/AlertDialog';
import { Footer } from './Footer';
import { usePageStore } from './PageStore';
import { HeaderDesktop, HeaderMobile } from './header';

import './Page.less';
import { ZnglyAppFallback } from '@/inertia/Shared/Page';

type Props = {
  children: React.ReactNode;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  footerStyle?: React.CSSProperties;
  mainFallback?: React.JSX.Element;
  footer?: boolean;
};

export const usePageFullScreen = (opts?: {
  resetOnUnmount?: boolean;
}) => {
  const headerVisible = usePageStore((state) => state.headerVisible);
  const footerVisible = usePageStore((state) => state.footerVisible);
  const dashboardTabsVisible = usePageStore((state) => state.dashboardTabsVisible);

  useEffect(() => {
    if (headerVisible === true) {
      usePageStore.setState({ headerVisible: false });
    }

    if (footerVisible === true) {
      usePageStore.setState({ footerVisible: false });
    }

    if (dashboardTabsVisible === true) {
      usePageStore.setState({ dashboardTabsVisible: false });
    }
  }, [headerVisible, footerVisible, dashboardTabsVisible]);

  useEffect(() => {
    return () => {
      if (opts?.resetOnUnmount === true) {
        usePageStore.setState({ headerVisible: true, footerVisible: true, dashboardTabsVisible: true });
      }
    };
  }, []);
};

export const setPageFullScreen = (type: 'on' | 'off') => {
  const value = type !== 'on';
  usePageStore.setState({ headerVisible: value, footerVisible: value, dashboardTabsVisible: value });
};

export const useNoHeader = () => {
  const headerVisible = usePageStore((state) => state.headerVisible);
  useEffect(() => {
    if (headerVisible === true) {
      usePageStore.setState({ headerVisible: false });
    }
  }, [headerVisible]);

  useEffect(() => {
    return () => {
      usePageStore.setState({ headerVisible: true });
    };
  }, []);
};

export const useNoFooter = () => {
  const footerVisible = usePageStore((state) => state.footerVisible);
  useEffect(() => {
    if (footerVisible === true) {
      usePageStore.setState({ footerVisible: false });
    }
  }, [footerVisible]);

  useEffect(() => {
    return () => {
      usePageStore.setState({ footerVisible: true });
    };
  }, []);
};

export const useNoDashboardTabs = () => {
  const dashboardTabsVisible = usePageStore((state) => state.dashboardTabsVisible);
  useEffect(() => {
    if (dashboardTabsVisible === true) {
      usePageStore.setState({ dashboardTabsVisible: false });
    }
  }, [dashboardTabsVisible]);

  useEffect(() => {
    return () => {
      usePageStore.setState({ dashboardTabsVisible: true });
    };
  }, []);
};

const Header = ({ style }: { style?: React.CSSProperties }) => {
  const headerVisible = usePageStore((state) => state.headerVisible);

  const { isMobile } = useBreakpoints();

  if (headerVisible === false) {
    return <></>;
  }

  return isMobile ? <HeaderMobile style={style} /> : <HeaderDesktop style={style} />;
};

const Page = ({ children, style, footer, ...props }: Props) => {
  const footerVisible = usePageStore((state) => state.footerVisible);
  const pageLoading = usePageStore((state) => state.pageLoading);

  const [alertDialog, setAlertDialog] = useState<AlertDialogProps>({
    title: '',
  });

  const alertDialogRef = useAlertDialogRef();

  // custom setter for default open state
  const toggleAlertDialog = useCallback((aProps: AlertDialogProps) => {
    setAlertDialog({ ...aProps });
    alertDialogRef?.current?.open();
  }, []);

  useEffect(() => {
    usePageStore.setState({ toggleAlertDialog });

    return () => {
      usePageStore.setState({ toggleAlertDialog: () => {} });
    };
  }, [alertDialogRef]);

  return (
    <>
      {pageLoading === true && (
        <LinearProgress sx={{ width: '100%', height: '3px', position: 'absolute', top: '0', zIndex: 10000 }} />
      )}

      <AlertDialog ref={alertDialogRef} {...alertDialog} />

      {/* Header component */}
      <Header style={props.headerStyle} />

      <Suspense fallback={props?.mainFallback ?? <ZnglyAppFallback bodyOnly />}>
        <Box className="z-page-body" style={style}>
          {children}
        </Box>
      </Suspense>

      {footerVisible === false ? <></> : <Footer style={props.footerStyle} />}
    </>
  );
};

export default Page;
